import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/clients/supabase';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial session check
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error("Session check error:", error);
          return;
        }

        setCurrentUser(session?.user || null);
      } catch (err) {
        console.error("Unexpected error during session check:", err);
      }
    };

    checkSession();
  }, []);

  // Listen for auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setCurrentUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch user profile and role when currentUser changes
  useEffect(() => {
    async function fetchUserProfile() {
      try {
        // If no user is logged in, reset states and return
        if (!currentUser) {
          setUserProfile(null);
          setLoading(false);
          return;
        }

        setLoading(true);
        // Add a small delay to allow the trigger to complete
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', currentUser.id)
          .single();

        if (error) {
          console.error('Error fetching user profile:', error);
          if (error.code === '42501') {
            console.log('Waiting for permissions to be properly set up...');
            // You might want to retry after a delay or handle this specific error differently
          }
          setUserProfile(null);
          return;
        }

        setUserProfile(profile);
      } catch (error) {
        console.error('Unexpected error fetching profile:', error);
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    }

    fetchUserProfile();
  }, [currentUser]);

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear all auth states
      setCurrentUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, options = {}) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: options.data,
          emailRedirectTo: window.location.origin + '/signin'
        }
      });

      await signOut();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    userName: userProfile?.username,
    userFirstName: userProfile?.first_name,
    userLastName: userProfile?.last_name,
    userRole: userProfile?.title,
    userPhone: userProfile?.phone_number,
    userProfile,
    signIn,
    signUp,
    signOut,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 